import '../../../node_modules/@wooorm/starry-night/style/dark.css'; // this is ugly but the only way Next.js will recognize it
import { getStarryNightInstance, type CodeBlockLanguage } from './StarryNightInstance';
import type { ElementContent, Root, RootContent } from 'hast';
import { UnreachableError } from '@/utils/UnreachableError';
import React, { Suspense, use } from 'react';
import { FloatingDelayGroup } from '../tooltip/FloatingUIClient';
import "./CodeBlock.linaria.module.css!=!./CodeBlock?./CodeBlock.linaria.module.css?css=H4sIAAAAAAAAA33QTU4CQRAF4L2nICEGXfQEZIykKuEixkX/1AwduqvGnhoBCXc3oMgCdFlvUS/vq5JrdrPd+2jpJdDyNVi1Jr3tvQysVExkXygTK6TIxEN2VLCTPmoUhkLJavwg7GwIkVuTqFGoKeOhuv0YwFEjhfYh9l2yO4h8fGxcEr/GTQy6gifK6CVJgfFisUClrRqbYstQYrvS37LTBbPqmTKaDbl1VDP0VExPibwCCxOaLJ+30v46vAq8sB6n/2A8XAgeLwbW9ZIGJVTpwMy6LZ4Qpv8Q2Eap3BY4V05Gk78qptX8uPjUMqd8Ruu2uKJvkZfpPTrr122RgQOM67rGw90Xj6iDVusBAAA="
const lineNumberStyles = "lbfy1yq";
const starryNightPromise = getStarryNightInstance();
export function CodeBlock({
  language,
  code,
  doLineNumbers = false
}: {
  readonly language: CodeBlockLanguage;
  readonly code: string;
  readonly doLineNumbers?: boolean;
}) {
  return <FloatingDelayGroup delay={300}><pre className={lineNumberStyles}>
		<code data-language={language}>
			<Suspense fallback={<HighlightCodeFallback language={language} code={code} doLineNumbers={doLineNumbers} />}>
				<HighlightCode language={language} code={code} doLineNumbers={doLineNumbers} />
			</Suspense>
		</code>
	</pre></FloatingDelayGroup>;
}
function HighlightCodeFallback({
  code,
  language,
  doLineNumbers
}: {
  readonly code: string;
  readonly language: CodeBlockLanguage;
  readonly doLineNumbers: boolean;
}) {
  const rootNode: Root = {
    type: 'root',
    children: [{
      type: 'text',
      value: code
    }]
  };
  if (doLineNumbers) createStarryNightGutter(rootNode);
  return <HighlightedCodeRenderer rootNode={rootNode} language={language} doLineNumbers={doLineNumbers} />;
}
function HighlightCode({
  language,
  code,
  doLineNumbers
}: {
  readonly language: CodeBlockLanguage;
  readonly code: string;
  readonly doLineNumbers: boolean;
}) {
  const starryNight = use(starryNightPromise);
  const root = starryNight.highlight(code, language);
  return <HighlightedCodeRenderer rootNode={root} language={language} doLineNumbers={doLineNumbers ?? false} />;
}
function HighlightedCodeRenderer({
  rootNode,
  language,
  doLineNumbers
}: {
  readonly rootNode: Root;
  readonly language: CodeBlockLanguage;
  doLineNumbers: boolean;
}) {
  if (doLineNumbers) createStarryNightGutter(rootNode);
  // eslint-disable-next-line react/no-array-index-key -- should never have to rerender this
  return rootNode.children.map((child, index) => <HighlightCodeToken key={index} node={child} language={language} />);
}
function HighlightCodeToken({
  node,
  language
}: {
  readonly node: RootContent;
  readonly language: CodeBlockLanguage;
}) {
  if (node.type === 'comment' || node.type === 'doctype') return null;
  if (node.type === 'text' || node.type === 'raw') return node.value;
  if (node.type !== 'element') throw new UnreachableError(node, 'Encountered an invalid node type while parsing Starry Night\'s Hast tree output');
  //console.log(node);
  if (node.properties.dataLineNumber) return <HighlightCodeLine node={node} language={language} />;
  return React.createElement(node.tagName, {
    className: node.properties.className
    // eslint-disable-next-line react/no-array-index-key -- should never have to rerender this
  }, node.children.map((child, index) => <HighlightCodeToken key={index} node={child} language={language} />));
}
function HighlightCodeLine({
  node,
  language
}: {
  readonly node: ElementContent & {
    type: 'element';
  };
  readonly language: CodeBlockLanguage;
}) {
  return <span data-l="">
		{
    // eslint-disable-next-line react/no-array-index-key -- should never have to rerender this
    node.children.map((child, index) => <HighlightCodeToken key={index} node={child} language={language} />)}
	</span>;
}

// The below code is taken from Starry Night's examples.
// Copyright (c) 2022 Titus Wormer <tituswormer@gmail.com>

export function createStarryNightGutter(tree: Root) {
  tree.data ??= {};

  // @ts-ignore -- use a custom prop so that we don't double-create the gutter (which can lead to hydration errors)
  if (tree.data.hasGutter) return;
  // @ts-ignore
  tree.data.hasGutter = true;
  const newChildren: RootContent[] = [];
  const lineBreakSearch = /\r?\n|\r/gu;
  let index = -1;
  let childAfterLastLineBreakChildIndex = 0;
  let unfinishedLineText = '';
  let lineNumber = 0;
  while (++index < tree.children.length) {
    const child = tree.children[index]!;
    if (child.type !== 'text') continue;
    let textStart = 0;
    let match = lineBreakSearch.exec(child.value);
    if (!match) continue;
    while (match) {
      // Nodes in this line.
      const line = tree.children.slice(childAfterLastLineBreakChildIndex, index) as ElementContent[];

      // Prepend text from a partial matched earlier text.
      if (unfinishedLineText) {
        line.unshift({
          type: 'text',
          value: unfinishedLineText
        });
        unfinishedLineText = '';
      }

      // Append text from this text.
      if (match.index > textStart) {
        line.push({
          type: 'text',
          value: child.value.slice(textStart, match.index)
        });
      }

      // Add a line, and the eol.
      lineNumber += 1;
      newChildren.push(createLine(line, lineNumber), {
        type: 'text',
        value: match[0]
      });
      childAfterLastLineBreakChildIndex = index + 1;
      textStart = match.index + match[0].length;
      match = lineBreakSearch.exec(child.value);
    }

    // Make sure to not drop the text after the last line ending!
    unfinishedLineText = child.value.slice(textStart);
  }
  const line = /** @type {Array<ElementContent>} */tree.children.slice(childAfterLastLineBreakChildIndex);
  // Prepend text from a partial matched earlier text.
  if (unfinishedLineText) {
    line.unshift({
      type: 'text',
      value: unfinishedLineText
    });
    unfinishedLineText = ''; // eslint-disable-line no-useless-assignment
  }
  if (line.length > 0) {
    lineNumber += 1;
    newChildren.push(createLine(line as ElementContent[], lineNumber));
  }

  // Replace children with new array.
  tree.children = newChildren;
}
function createLine(children: ElementContent[], line: number): ElementContent {
  //console.log('createLine', line, children);
  return {
    type: 'element',
    tagName: 'span',
    properties: {
      dataLineNumber: line
    },
    children
  };
}